import FormattedCurrencyNumber from 'common/formatted-currency-number/formatted-currency-number';
import Table from 'common/table/table';
import { Icons, RowIconAction, ThemeHeader } from 'common/table/table-helper';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedNumber, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Div, Icon } from 'ui';
import moment from 'utils/cops-moment';

const OrderIcon = styled(Icon)`
  color: ${p => p.orderType === 'main' ? '#ff9600' : '#0078a1'};
  margin: 0 10px;
  vertical-align: bottom;
`;

const Wrapper = styled(Div)`
  box-shadow: rgba(0, 0, 0, 0.27) 0px;
  border-radius: 4px;
  background-color: #fff;
  margin-bottom: 20px;
`;

const SubOrderTable = ({ intl, onOpenSubOrder, subOrders }) => {
  const toCurrency = (value, currency) => currency ?
    <FormattedCurrencyNumber
      value={value}
      style="currency" // eslint-disable-line
      currency={currency}
    /> :
    <FormattedNumber
      value={value}
    />;

  const getSubOrdersColumns = () => {
    const { formatMessage: translate } = intl;

    return [
      {
        id: 'actions',
        displayName: '',
        fieldName: '',
        width: '30px',
        renderFn: r => <Icons>
          <RowIconAction
            title={intl.formatMessage({ id: 'order.tooltip.viewSubOrder' })}
            tooltipKey='view'
            iconKey='eye'
            action={() => onOpenSubOrder(r.id)}
            disabled={false}
          />
        </Icons>,
      },
      {
        id: 'technicianName',
        displayName: translate({ id: 'order.fieldEngineer.FSE' }),
        fieldName: 'technicianName',
        width: '300px',
        tooltip: row => row.technicianName,
      },
      {
        id: 'statusInfo.label',
        displayName: translate({ id: 'order.fieldEngineer.ORDER_STATUS' }),
        fieldName: 'statusInfo.label',
        renderFn: record =>
          <FormattedMessage id={`order.status.${record.statusInfo && record.statusInfo.name}`} />
        ,
      },
      {
        id: 'scheduledDate',
        displayName: translate({ id: 'order.fieldEngineer.PLANNED_DATE' }),
        fieldName: 'scheduledDate',
        renderFn: value => moment(value.scheduledDate).format('DD/MM/YYYY'),
      },
      {
        id: 'labor',
        displayName: translate({ id: 'order.fieldEngineer.LABOR' }),
        fieldName: 'labor',
        renderFn: record => `${record.actualLabor}/${record.plannedLabor}`,
      },
      {
        id: 'cost',
        displayName: translate({ id: 'order.fieldEngineer.COST' }),
        fieldName: 'cost',
        renderFn: record =>
          <span>
            {toCurrency(record.actualCost, record.costCurrency)}
            /
            {toCurrency(record.plannedCost, record.costCurrency)}
          </span>
        ,
      },
      {
        id: 'delivered',
        displayName: translate({ id: 'order.fieldEngineer.PARTS_DELIVERED_STATUS' }),
        fieldName: 'delivered',
        renderFn: record =>
          translate({
            id: `order.fieldEngineer.hasAllPartsDelivered.${record.hasAllPartsDelivered}`,
          }),
      },
      {
        id: 'number',
        displayName: translate({ id: 'order.fieldEngineer.ORDER_NUMBER' }),
        fieldName: 'number',
      },
      {
        id: 'suborderIcons',
        displayName: '',
        fieldName: '',
        width: '60px',
        renderFn: ({ isSubOrder }) => {
          if (isSubOrder) {
            return <OrderIcon type='link' />;
          }
          return <OrderIcon orderType='main' type='crown' />;
        },
      },
    ];
  };

  const renderHeader = () => <ThemeHeader>
    {intl.formatMessage({
      id: 'order.fieldEngineer.ADDITIONAL_ENGINEERS',
    })}
  </ThemeHeader>;

  if (!subOrders || !subOrders.length) {
    return null;
  }

  return (
    <Wrapper>
      <Table
        labelInColumn={true}
        columns={getSubOrdersColumns()}
        data={subOrders}
        fullWidth={true}
        renderCustomHeader={renderHeader}
      />
    </Wrapper>
  );
};

SubOrderTable.propTypes = {
  intl: PropTypes.object,
  onOpenSubOrder: PropTypes.func,
  subOrders: PropTypes.array,
};

export default injectIntl(SubOrderTable);
